import { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";

const completeStoredMindText = (text: string): string => {
  const value = text.trim();
  const characters = [...value];
  if (characters.length < 240) return value;
  const clipped = characters.slice(0, 240);
  for (let index = clipped.length - 1; index >= 0; index -= 1) {
    if (!".!?".includes(clipped[index] ?? "")) continue;
    const next = clipped[index + 1];
    if (next?.trim()) continue;
    return clipped
      .slice(0, index + 1)
      .join("")
      .trim();
  }
  return "";
};

const mindText = (texts: readonly string[]): string =>
  [...new Set(texts.map(completeStoredMindText).filter(Boolean))].join("\n\n");

export const NikolaMind = ({ texts, active }: { texts: readonly string[]; active: boolean }) => {
  const content = mindText(texts);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });
  const [visibleLength, setVisibleLength] = useState(() => (active && !reduceMotion ? 0 : content.length));

  useEffect(() => {
    if (!active || !content || reduceMotion) {
      setVisibleLength(content.length);
      return;
    }
    setVisibleLength((current) => Math.min(current, content.length));
    const interval = window.setInterval(
      () =>
        setVisibleLength((current) => {
          const next = Math.min(content.length, current + 2);
          if (next === content.length) window.clearInterval(interval);
          return next;
        }),
      24,
    );
    return () => window.clearInterval(interval);
  }, [active, content, reduceMotion]);

  if (!content) return null;
  const typing = active && !reduceMotion && visibleLength < content.length;
  return (
    <Box
      component="details"
      data-testid="nikola-mind"
      data-nikola-typewriter={active ? "active" : "idle"}
      open={active}
      sx={{
        mb: 1.15,
        pb: 0.85,
        borderBottom: "1px solid",
        borderColor: "rgba(240,236,226,.18)",
        "& > summary": {
          cursor: "pointer",
          color: "text.secondary",
          fontSize: ".64rem",
          fontWeight: 850,
          letterSpacing: ".055em",
        },
        "&[open] > summary": { mb: 0.7 },
      }}
    >
      <Box component="summary">Nikola&apos;s mind</Box>
      <Typography component="div" sx={{ color: "text.secondary", fontSize: ".76rem", lineHeight: 1.65 }}>
        {active ? (
          <Box
            component="span"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            sx={{ position: "absolute", width: 1, height: 1, m: -1, overflow: "hidden", clip: "rect(0 0 0 0)" }}
          >
            {content}
          </Box>
        ) : null}
        <Box component="span" aria-hidden={active || undefined} sx={{ whiteSpace: "pre-wrap" }}>
          {active ? content.slice(0, visibleLength) : content}
        </Box>
        {typing ? (
          <Box
            component="span"
            aria-hidden
            sx={{
              display: "inline-block",
              width: 2,
              height: ".9em",
              ml: 0.3,
              bgcolor: "primary.main",
              verticalAlign: "-.08em",
              animation: "nikola-mind-caret .72s steps(1,end) infinite",
              "@keyframes nikola-mind-caret": { "0%, 45%": { opacity: 1 }, "46%, 100%": { opacity: 0 } },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        ) : null}
      </Typography>
    </Box>
  );
};
